import React, { useEffect, useState } from "react";
import { Spinner } from "reactstrap";
import Checkbox from "../../../base/components/Checkbox";
import BiomarkersService from "../../../services/BiomarkersService";
import {SEX_LABELS} from "../../../groups/app/Fitness/WorkoutDetails/components/WorkoutDescriptionTable/constants.js";
import {BiomarkerCategoriesByRule, BiomarkerTypes} from "../../../groups/app/Biomarkers/CreateEdit/constants.js";

function groupByCategory(biomarkers) {
  const grouped = {};
  biomarkers.forEach(b => {
    const cat = b.category;
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(b);
  });
  return grouped;
}

const BIOMARKER_DEPENDENCIES = {
  "Non HDL Chol": ["Cholesterol", "HDL Chol"],
  "TC:HDL ratio": ["Cholesterol", "HDL Chol"],
  "LDL Chol": ["Cholesterol", "HDL Chol", "Triglyceride"],
  "Free Test": ["SHBG", "Testosterone", "Albumin"],
  "Globulin": ["Protein", "Albumin"],
  "eGFR": ["Creatinine"],
  "ApoB:A1": ["ApoA1", "ApoB"]
};

export default function StepSelectBiomarkers({
  selectedType,
  kitObjectId,
  selectedBiomarkers,
  setSelectedBiomarkers,
  UserSex
}) {
  const [loading, setLoading] = useState(true);
  const [biomarkers, setBiomarkers] = useState([]);

  useEffect(() => {
    const fetchBiomarkers = async () => {
      setLoading(true);
      const gender = SEX_LABELS[UserSex].toLowerCase();
      let params = {
        limit: 100,
        offset: 0,
        orderBy: "categoryName",
        orderType: "asc",
        gender,
        onlyRowData: true,
        onlyActive: true
      };
      if (selectedType !== "both") params.tube = selectedType;
      const service = new BiomarkersService();
      const res = await service.getBiomarkers(params);

      const allowedCategories = BiomarkerCategoriesByRule[BiomarkerTypes.bloodRule];
      let filtered = (res?.data || []).filter(
        b => allowedCategories.includes(b.category?.name)
      );

      filtered = filtered.map(b => ({
        ...b,
        category: {
          ...b.category,
          name:
            b.category?.name === "Hormones & Energy (female)" ||
            b.category?.name === "Hormones & Energy (male)"
              ? "Hormones & Energy"
              : b.category?.name
        }
      }));

      setBiomarkers(filtered);
      setLoading(false);
    };
    fetchBiomarkers();
  }, [selectedType, kitObjectId]);

  const isSelected = (id) =>
    selectedBiomarkers.some(b => b.id === id);

  const getRequiredByBiomarkers = (id) => {
    const biomarker = biomarkers.find(b => b.id === id);
    if (!biomarker || !isSelected(id)) return [];

    const biomarkerShortName = biomarker.shortName;
    const requiredBy = [];

    for (const [calculatedShortName, dependencies] of Object.entries(BIOMARKER_DEPENDENCIES)) {
      if (dependencies.includes(biomarkerShortName)) {
        const calculatedBiomarker = biomarkers.find(b => b.shortName === calculatedShortName);
        if (calculatedBiomarker && isSelected(calculatedBiomarker.id)) {
          requiredBy.push(calculatedBiomarker.name);
        }
      }
    }
    return requiredBy;
  };

  const isDisabled = (id) => {
    return getRequiredByBiomarkers(id).length > 0;
  };

  const toggleBiomarker = (id) => {
    const biomarker = biomarkers.find(b => b.id === id);
    if (!biomarker) return;

    if (isDisabled(id)) return;

    const biomarkerShortName = biomarker.shortName;

    if (isSelected(id)) {
      const biomarkersToRemove = [id];
      
      const dependencies = BIOMARKER_DEPENDENCIES[biomarkerShortName] || [];
      dependencies.forEach(depShortName => {
        const depBiomarker = biomarkers.find(b => b.shortName === depShortName);
        if (depBiomarker && isSelected(depBiomarker.id)) {
          let stillRequired = false;
          for (const [calcShortName, calcDependencies] of Object.entries(BIOMARKER_DEPENDENCIES)) {
            if (calcShortName !== biomarkerShortName && calcDependencies.includes(depShortName)) {
              const calcBiomarker = biomarkers.find(b => b.shortName === calcShortName);
              if (calcBiomarker && isSelected(calcBiomarker.id)) {
                stillRequired = true;
                break;
              }
            }
          }
          if (!stillRequired) {
            biomarkersToRemove.push(depBiomarker.id);
          }
        }
      });

      setSelectedBiomarkers(
        selectedBiomarkers.filter(b => !biomarkersToRemove.includes(b.id))
      );
    } else {
      const biomarkersToAdd = [
        {
          id: biomarker.id,
          name: biomarker.name,
          category: biomarker.category?.name
        }
      ];

      const dependencies = BIOMARKER_DEPENDENCIES[biomarkerShortName] || [];
      dependencies.forEach(depShortName => {
        const depBiomarker = biomarkers.find(b => b.shortName === depShortName);
        if (depBiomarker && !isSelected(depBiomarker.id)) {
          biomarkersToAdd.push({
            id: depBiomarker.id,
            name: depBiomarker.name,
            category: depBiomarker.category?.name
          });
        }
      });

      setSelectedBiomarkers([...selectedBiomarkers, ...biomarkersToAdd]);
    }
  };

  const selectedGrouped = groupByCategory(selectedBiomarkers);

  if (loading) {
      return (
      <div className="d-flex my-2 justify-content-center">
        <Spinner color="primary"/>
      </div>
    );
  }
  if (!biomarkers.length) return <div>No biomarkers found.</div>;

  return (
    <div className="row">
      <div className="col-md-8">
        {Object.entries(groupByCategory(
          biomarkers.map(b => ({
            id: b.id,
            name: b.name,
            category: b.category?.name,
            label: b.label,
            shortName: b.shortName
          }))
        )).map(([cat, items]) => (
          <div key={cat} className="mb-4">
            <h6 className="fw-bold text-uppercase">
              {cat}
            </h6>
            <hr className="my-2"/>
            <div>
              {items.map(b => {
                const disabled = isDisabled(b.id);
                const requiredBy = getRequiredByBiomarkers(b.id);
                return (
                  <div
                    key={b.id}
                    className={`d-flex align-items-center mb-2 p-2 rounded ${disabled ? "opacity-50" : "cursor-pointer"} ${isSelected(b.id) ? "bg-light" : ""}`}
                    style={{ 
                      border: "1px solid #e5e7eb",
                      cursor: disabled ? "not-allowed" : "pointer"
                    }}
                    onClick={() => !disabled && toggleBiomarker(b.id)}
                  >
                    <Checkbox
                      id={b.id}
                      value={isSelected(b.id)}
                      onChange={() => !disabled && toggleBiomarker(b.id)}
                      disabled={disabled}
                      className="me-2"
                    />
                    <div>
                      <div className="fw-bold">{b.name}</div>
                      <div className="text-muted">{b.label || b.shortName || ""}</div>
                      {disabled && requiredBy.length > 0 && (
                        <div className="text-black fw-bold small" style={{ fontStyle: "italic" }}>
                          Required for calculation: {requiredBy.join(", ")}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div
        className="col-md-4"
        style={{
          position: "sticky",
          top: 1,
          background: "#fff",
          zIndex: 2,
          alignSelf: "flex-start"
        }}>
        <div style={{ border: "1px solid #2563eb", borderRadius: 8, padding: 16, background: "#f7f9fc" }}>
          <div className="fw-bold text-primary mb-1">
            SELECTED BIOMARKERS
          </div>
          <div className="mb-2">
            {selectedBiomarkers.length} selected
          </div>
          {selectedBiomarkers.length === 0 ? (
            <div
              className="d-flex flex-column align-items-center justify-content-center"
              style={{ minHeight: 46, color: "#64748b" }}>
              No biomarkers selected
            </div>
          ) : (
            <div style={{ maxHeight: 258, overflowY: "auto", overflowX: "hidden" }}>
              {
                Object.entries(selectedGrouped).map(([cat, items]) => (
                  <div key={cat} className="mb-2">
                    <div className="fw-bold">{cat}</div>
                    <ul className="ps-3 mb-0">
                      {items.map(b => (
                        <li className="mb-1" key={b.id}>
                          {b.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))
              }
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
